import React, { useState, useEffect } from 'react';
import TermsConsentFields from '../components/shared/legal/TermsConsentFields';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { useAuthStore } from '../stores/useAuthStore';
import { Logo } from '../components/icons/Logo';
import { EyeIcon, EyeOffIcon } from '../components/icons/Icons';
import toast from 'react-hot-toast';
import { Input, Button } from '../components/ui';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import { INVITATION_TOKEN_KEY } from '../constants/storageKeys';

const RegisterPage: React.FC = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
    const [registerError, setRegisterError] = useState<string>('');
    // LGPD: aceite ativo (desmarcado por padrão) e autorização opcional de imagens.
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [marketingImagesConsent, setMarketingImagesConsent] = useState(false);
    const [inviteToken, setInviteToken] = useState<string | null>(null);
    const [inviteInfo, setInviteInfo] = useState<{ companyName: string; role: string; inviterName: string } | null>(null);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const login = useAuthStore((state) => state.login);
    const prefersReducedMotion = useReducedMotion();

    // Page-load entrance variants: base=300ms, slow=500ms — numeric equivalents of
    // --snapfy-duration-base and --snapfy-duration-slow; easing mirrors --snapfy-ease-standard.
    const headingVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: -12 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const } },
    };
    const cardVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const, delay: 0.15 } },
    };

    useEffect(() => {
        // Prefer the URL param, but fall back to the token stored by
        // AcceptInvitePage. Without this fallback, if the ?invite= param is lost
        // anywhere in the accept -> register redirect chain, the invite is never
        // sent to /auth/register and the user registers without joining the
        // company (invite stays pending, company_id NULL).
        const token = searchParams.get('invite') || localStorage.getItem(INVITATION_TOKEN_KEY);
        if (token) {
            setInviteToken(token);
            localStorage.setItem(INVITATION_TOKEN_KEY, token);

            // Fetch invite info
            // We use direct axios or api client here. Since it's public (sometimes), we need to ensure api client handles it.
            // Our api client adds auth token if present, but this endpoint is public.
            // We need to import 'api' from '../services/api'
            import('../services/api').then(({ api }) => {
                api.get<{ companyName: string; role: string; inviterName: string }>(`/company/invites/${token}`)
                    .then(res => {
                        setInviteInfo(res.data);
                        // Pre-fill email if returned (api currently returns email)
                        // But TypeScript needs type adjustment. Let's assume response structure.
                        // Our backend returns { email, company_name, role, inviter_name }
                        // @ts-ignore
                        if (res.data.email) setEmail(res.data.email);
                    })
                    .catch(err => console.error('Invalid invite token', err));
            });
        }
    }, [searchParams]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationErrors({}); // Limpar erros anteriores
        setRegisterError(''); // Limpar erro geral anterior

        if (password !== passwordConfirmation) {
            setValidationErrors({
                password_confirmation: ['As senhas não coincidem']
            });
            toast.error('As senhas não coincidem.');
            return;
        }
        setIsLoading(true);
        try {
            const response = await AuthService.register({
                name,
                email,
                company: inviteToken ? 'Joining Company' : company, // Dummy company name if joining
                role: 'user',
                password,
                confirmPassword: passwordConfirmation,
                inviteToken: inviteToken || undefined,
                acceptTerms,
                marketingImagesConsent,
            });
            login(response.user);
            if (inviteToken) {
                // The invite was passed to /auth/register, which auto-joins the
                // company. Clear the stored token so a later unrelated register
                // doesn't reuse it, and surface the outcome instead of failing
                // silently (the backend returns 201 even when the join fails).
                localStorage.removeItem(INVITATION_TOKEN_KEY);
                if (response.user?.companyId) {
                    toast.success('Conta criada e você entrou na empresa! Verifique seu email para ativar a conta.');
                } else {
                    toast.error('Conta criada, mas não foi possível aplicar o convite. Abra o link do convite novamente já logado para entrar na empresa.');
                }
            } else {
                toast.success('Conta criada! Verifique seu email para receber seus 10 créditos grátis.');
            }
            navigate('/verify-email');
        } catch (error: any) {
            console.error(error);

            // Capturar erros de validação (AuthService adiciona validationErrors)
            if (error.validationErrors) {
                setValidationErrors(error.validationErrors);
                toast.error('Por favor, corrija os erros no formulário.');
            } else {
                const errorMessage = error.message || 'Falha no registro. Verifique seus dados.';
                setRegisterError(errorMessage);
                toast.error(errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-snapfy-bg flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <motion.div
                className="sm:mx-auto sm:w-full sm:max-w-md"
                variants={headingVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="flex justify-center">
                    <Logo className="h-12 w-auto" />
                </div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-snapfy-text-primary">
                    {inviteInfo ? `Junte-se a ${inviteInfo.companyName}` : 'Crie sua conta'}
                </h2>
                {inviteInfo && (
                    <p className="mt-2 text-center text-sm text-green-400">
                        Você foi convidado por {inviteInfo.inviterName}
                    </p>
                )}
                <p className="mt-2 text-center text-sm text-snapfy-text-secondary">
                    Já tem uma conta?{' '}
                    <Link to="/login" className="font-medium text-snapfy-primary hover:text-snapfy-accent-soft">
                        Faça login
                    </Link>
                </p>
            </motion.div>

            <motion.div
                className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="card bg-snapfy-surface py-8 px-4 shadow-card rounded-card sm:px-10 border border-snapfy-border">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <Input
                            id="name"
                            name="name"
                            type="text"
                            label="Nome Completo"
                            autoComplete="name"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            error={validationErrors.name?.[0]}
                        />

                        <Input
                            id="email"
                            name="email"
                            type="email"
                            label="Endereço de Email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            // Input does not forward refs; disabled prop passed directly via spread
                            disabled={!!inviteInfo?.email}
                            error={validationErrors.email?.[0]}
                        />

                        {!inviteToken && (
                            <Input
                                id="company"
                                name="company"
                                type="text"
                                label="Empresa"
                                required
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                                error={validationErrors.company?.[0]}
                            />
                        )}

                        <Input
                            id="password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            label="Senha"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={validationErrors.password?.[0]}
                            trailingAffordance={
                                <button
                                    type="button"
                                    className="flex items-center text-snapfy-text-secondary hover:text-snapfy-text-primary"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                >
                                    {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                </button>
                            }
                        />

                        {/* password_confirmation: no toggle — confirm fields conventionally stay masked */}
                        <Input
                            id="password_confirmation"
                            name="password_confirmation"
                            type="password"
                            label="Confirmar Senha"
                            required
                            value={passwordConfirmation}
                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                            error={validationErrors.password_confirmation?.[0]}
                        />

                        {registerError && (
                            <div className="rounded-md bg-snapfy-error/10 border border-snapfy-error/50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-snapfy-error" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-snapfy-error font-medium">{registerError}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <TermsConsentFields
                            idPrefix="register-page-consent"
                            accepted={acceptTerms}
                            onAcceptedChange={setAcceptTerms}
                            marketingImages={marketingImagesConsent}
                            onMarketingImagesChange={setMarketingImagesConsent}
                            error={validationErrors.accept_terms?.[0]}
                        />

                        <div>
                            <Button
                                type="submit"
                                variant="solid"
                                className="w-full"
                                isLoading={isLoading}
                                disabled={isLoading || !acceptTerms}
                            >
                                {inviteToken ? 'Aceitar Convite' : 'Criar Conta'}
                            </Button>
                        </div>
                    </form>

                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-snapfy-border" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-snapfy-surface text-snapfy-text-secondary">
                                    Ou registre-se com
                                </span>
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3">
                            <div>
                                <a
                                    href={AuthService.getSocialLoginUrl('google')}
                                    className="w-full inline-flex justify-center py-2 px-4 border border-snapfy-border rounded-md shadow-sm bg-snapfy-surface-hover text-sm font-medium text-snapfy-text-secondary hover:bg-snapfy-inner transition-colors"
                                >
                                    <span className="sr-only">Registrar com Google</span>
                                    <svg className="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
                                    </svg>
                                </a>
                            </div>

                            <div>
                                <a
                                    href={AuthService.getSocialLoginUrl('github')}
                                    className="w-full inline-flex justify-center py-2 px-4 border border-snapfy-border rounded-md shadow-sm bg-snapfy-surface-hover text-sm font-medium text-snapfy-text-secondary hover:bg-snapfy-inner transition-colors"
                                >
                                    <span className="sr-only">Registrar com GitHub</span>
                                    <svg className="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default RegisterPage;
